/* The 11 SEO actions, their routing, effort tiers and the impact model.
   Extracted verbatim from artifact.html <script id="app"> (action engine).

   LOAD-BEARING INVARIANT — recover vs capture:
   RECOVER actions (expand, win, answer, usage, faq, commercial) already rank, so the
   CTR-gap math is defensible and they report "+N clicks/month recoverable".
   CAPTURE actions (compare, choose, solve, plans) describe content that does not exist
   yet, so they report "N impressions/month of demand addressed" and must NEVER show a
   click projection. Do not merge the two into a single "estimated clicks" number. */

import { expectedCtr } from "./score.js";

export const ACTIONS = {
  compare:    { icon: "compare", title: "Create comparison content",       desc: "People are weighing options. Build dedicated comparison pages with explicit criteria tables." },
  choose:     { icon: "star",    title: "Add recommendation sections",     desc: "Best / which-one demand. Add clear picks with selection criteria to existing pages." },
  solve:      { icon: "tool",    title: "Publish problem-solving content", desc: "Symptom-first structure: question-worded H2, direct 2–3 sentence answer, then detail." },
  usage:      { icon: "guide",   title: "Add usage & how-to sections",     desc: "How-to and post-purchase questions. Expand product pages with usage, dosage and suitability." },
  plans:      { icon: "list",    title: "Publish plans, lists & templates", desc: "People ask for routines, checklists and plans. Give them copyable, structured formats." },
  faq:        { icon: "help",    title: "Add FAQ / reassurance blocks",    desc: "Safety, validation and clarification questions. Short direct answers on relevant pages." },
  commercial: { icon: "cart",    title: "Strengthen commercial pages",     desc: "Where-to-buy and transaction-adjacent queries. Tighten product info, availability and pricing clarity." },
  expand:     { icon: "up",      title: "Expand content to lift rankings", desc: "High-impression questions ranking beyond position 8. Answer them directly on the ranking page." },
  win:        { icon: "click",   title: "Rewrite to win the click",        desc: "Impressions but zero clicks — the snippet doesn't answer the question as asked. Fix the first sentence." },
  answer:     { icon: "answer",  title: "Answer-first content",            desc: "General conversational demand. Lead every relevant section with the direct answer." },
  clean:      { icon: "clean",   title: "Clean your reporting",            desc: "Tracker probes, pasted strings and junk inflate query counts. Exclude these buckets before reporting demand." },
};

export const ACTION_WHY = {
  compare: "Comparison searchers are close to choosing. Whoever frames the criteria wins the decision - and increasingly, the AI answer.",
  choose: "Best-of demand carries the highest commercial intent in the conversational set. Clear picks convert; vague category pages don't.",
  solve: "Problem searches are high-trust moments. Answering the symptom directly builds authority the rest of the site inherits.",
  usage: "Usage questions come from buyers and owners. Answering them lifts product-page relevance and reduces support load.",
  plans: "People asking for plans want a format, not an article. Copyable structures earn links, saves and AI citations.",
  faq: "Safety and validation questions block purchases silently. Short direct answers remove the last objection.",
  commercial: "Transaction-adjacent queries are the cheapest revenue in the dataset - the demand already exists.",
  expand: "These queries already earn impressions; Google just doesn't trust the page enough yet. Expansion is faster than new content.",
  win: "Ranking is done; the snippet is failing. Rewriting the first sentence is the highest ROI-per-hour fix on this list.",
  answer: "Conversational demand rewards pages that answer first and explain second - in classic results and AI features alike.",
  clean: "Junk buckets inflate query counts and distort share metrics. Clean data is the prerequisite for every other decision here.",
};

export const ACTION_TARGET = {
  compare: "New comparison pages, or criteria tables on existing category pages",
  choose: "Recommendation blocks on category and guide pages",
  solve: "Symptom-first guide content (question-worded H2s)",
  usage: "Product pages - usage, dosage, suitability, FAQ blocks",
  plans: "Standalone plan / checklist / template content",
  faq: "FAQ sections on the relevant product and guide pages",
  commercial: "Product pages - pricing, availability, purchase paths",
  expand: "The pages currently ranking for these queries",
  win: "Ranking snippets - titles, meta, first paragraph",
  answer: "All pages receiving conversational demand",
  clean: "GSC reports and dashboards - bucket exclusion filters",
};

/* recover = queries already rank, CTR-gap math is defensible;
   capture = content doesn't exist, no click projection */
export const RECOVER = { expand: 1, win: 1, answer: 1, usage: 1, faq: 1, commercial: 1 };

export const EFFORT = {
  win: [1, "On-page"],
  expand: [2, "Content edit"], usage: [2, "Content edit"], faq: [2, "Content edit"],
  answer: [2, "Content edit"], commercial: [2, "Content edit"],
  compare: [3, "New content"], choose: [3, "New content"], plans: [3, "New content"], solve: [3, "New content"],
  clean: [4, "Process"],
};

export const FORMAT_HINT = {
  compare: "comparison table",
  choose: "ranked list + clear verdict",
  solve: "symptom-first guide",
  plans: "step plan / checklist",
  usage: "usage FAQ on the product page",
  faq: "Q&A block",
  commercial: "product page section",
  expand: "expanded section on the ranking page",
  win: "rewritten title, meta & first paragraph",
  answer: "answer-first intro",
};

/* Routes one classified row to exactly one action, or null. */
export function actionOf(d) {
  if (["tracker_probe", "pasted_string", "long_uncategorised"].includes(d.bucket)) return "clean";
  const st = d.stage || "";
  const convish = d.bucket === "conversational" || d.bucket === "uncertain";
  if (!convish && !st) return null;
  if (st === "Comparison" || d.qt === "compare") return "compare";
  if (["Recommendation", "Decision Support", "Alternative Seeking"].includes(st) || d.need === "choose") return "choose";
  if (["Troubleshooting", "Problem Identification", "Solution Seeking", "Diagnosis / Identification", "Cause Investigation"].includes(st)) return "solve";
  if (["How-to / Procedure", "Post-Purchase"].includes(st)) return "usage";
  if (["Planning", "Generation", "Transformation", "Instruction / Action", "Example Request", "Summarisation", "Synthesis"].includes(st)) return "plans";
  if (["Validation", "Verification", "Evaluation", "Feedback / Review", "Clarification"].includes(st)) return "faq";
  if (st === "Purchase / Transaction") return "commercial";
  if (d.pos > 8 && d.imp >= 10) return "expand";
  if (d.imp >= 20 && d.clicks === 0) return "win";
  if (convish) return "answer";
  return null;
}

export function actionGroups(rows) {
  const groups = {};
  for (const d of rows) if (d.action) (groups[d.action] = groups[d.action] || []).push(d);
  return groups;
}

export function actionStats(qs) {
  const imp = qs.reduce((s2, d) => s2 + d.imp, 0);
  const clicks = qs.reduce((s2, d) => s2 + d.clicks, 0);
  const pos = imp ? qs.reduce((s2, d) => s2 + d.pos * d.imp, 0) / imp : 0;
  let potential = 0;
  for (const d of qs) {
    const target = expectedCtr(Math.min(d.pos, 5));
    potential += Math.max(0, d.imp * target / 100 - d.clicks);
  }
  return { imp, clicks, pos, potential: Math.round(potential) };
}

/* Priority label. Colour for each label is mapped in the client. */
export function actionPriority(k, st, allImp) {
  if (k === "clean") return "Hygiene";
  const share = st.imp / Math.max(1, allImp);
  if (k === "win" && st.imp >= 100) return "High";
  if (share >= 0.18 || (st.pos > 8 && share >= 0.10)) return "High";
  if (share >= 0.07) return "Medium";
  return "Low";
}

/* The impact model, in one place, so the recover/capture split cannot drift.
   Returns exactly one of: hygiene (no number), recover (clicks), capture (impressions). */
export function impactOf(k, st) {
  if (k === "clean") return { type: "hygiene", value: null, unit: null };
  if (RECOVER[k]) return { type: "recover", value: st.potential, unit: "clicks/mo recoverable" };
  return { type: "capture", value: st.imp, unit: "impressions/mo of demand addressed" };
}
